
// YouTube API constants
export const YOUTUBE_API_KEY = 'AIzaSyDpGHtxljv53mfrJvhbjF8dTJk072D8iPM';
export const YOUTUBE_CHANNEL_ID = 'UCoH9CNsk2GKi0YGLflUBAVg'; // Exodus ADV channel ID

export interface YouTubeVideo {
  id: string;
  title: string;
  description: string;
  publishedAt: string;
  thumbnail: {
    url: string;
    width: number;
    height: number;
  };
  statistics?: {
    viewCount: string;
    likeCount: string;
  };
}

/**
 * Fetch latest videos from a YouTube channel using the direct endpoint
 */
export async function fetchLatestYouTubeVideos(maxResults: number = 6): Promise<YouTubeVideo[]> {
  try {
    // Use the exact API endpoint requested
    const response = await fetch(
      `https://www.googleapis.com/youtube/v3/search?key=${YOUTUBE_API_KEY}&channelId=${YOUTUBE_CHANNEL_ID}&part=snippet,id&order=date&maxResults=${maxResults}&type=video`
    );

    if (!response.ok) {
      console.error('YouTube API error:', await response.text());
      throw new Error('Failed to fetch YouTube videos');
    }

    const data = await response.json();
    
    if (!data.items || data.items.length === 0) {
      console.warn('No YouTube videos found in search results');
      throw new Error('No videos found');
    }

    // Transform the response into our YouTubeVideo format
    return data.items.map((item: any) => ({
      id: item.id.videoId,
      title: item.snippet.title,
      description: item.snippet.description,
      publishedAt: item.snippet.publishedAt,
      thumbnail: {
        url: item.snippet.thumbnails.high ? item.snippet.thumbnails.high.url : item.snippet.thumbnails.default.url,
        width: item.snippet.thumbnails.high ? item.snippet.thumbnails.high.width : 120,
        height: item.snippet.thumbnails.high ? item.snippet.thumbnails.high.height : 90,
      },
    }));
  } catch (error) {
    console.error('Error fetching YouTube videos:', error);
    throw error;
  }
}

/**
 * Fetch most popular videos from a YouTube channel
 */
export async function fetchPopularYouTubeVideos(maxResults: number = 6): Promise<YouTubeVideo[]> {
  try {
    // For now, we'll just fetch latest videos
    // In a production environment, you'd want to implement proper sorting by view count
    return await fetchLatestYouTubeVideos(maxResults);
  } catch (error) {
    console.error('Error fetching popular YouTube videos:', error);
    throw error;
  }
}

/**
 * Format view count to human-readable format
 */
export function formatViewCount(viewCount: string): string {
  const count = parseInt(viewCount, 10);
  
  if (count >= 1000000) {
    return `${(count / 1000000).toFixed(1)}M views`;
  } else if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}K views`;
  }
  
  return `${count} views`;
}

/**
 * Format YouTube publish date to relative time
 */
export function formatPublishDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - date.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays < 1) {
    return 'Today';
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
  } else if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return `${months} ${months === 1 ? 'month' : 'months'} ago`;
  } else {
    const years = Math.floor(diffDays / 365);
    return `${years} ${years === 1 ? 'year' : 'years'} ago`;
  }
}
